import React, {useEffect, useState} from 'react';
import {Button} from "primereact/button";
import {Tag} from "primereact/tag";

import {DynamicPricingAuditService} from "../../service/DynamicPricingAuditService";
import {toRelativeTimeString} from "../../utils/TimeUtil";
import {actionLabel, changeSummary, scopeLabel, scopeSeverity} from "./dynamicPricingAuditLabels";
import DynamicPricingAuditDialog from "./DynamicPricingAuditDialog";

const auditService = new DynamicPricingAuditService();
const PREVIEW_SIZE = 5;

// Shows who changed dynamic pricing, when, at which level and to what values.
// Only the campaign's own records plus the template changes that reach its
// products; the dialog can widen that to the whole account.
const DynamicPricingAuditCard = ({t, campaignId, reloadKey}) => {
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(false);
    const [showAll, setShowAll] = useState(false);

    useEffect(() => {
        if (!campaignId) return;
        setLoading(true);
        auditService.list({campaign_id: campaignId, limit: PREVIEW_SIZE, offset: 0})
            .then(data => { setRows(data?.rows || []); })
            .catch(() => { setRows([]); })
            .finally(() => setLoading(false));
    }, [campaignId, reloadKey]);

    return (
        <div style={{border: '1px solid #e5e7eb', borderRadius: 10, padding: 16,
                     background: '#fff', marginTop: 14}}>
            <div style={{display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                         gap: 8, marginBottom: 10}}>
                <span style={{fontWeight: 700, display: 'flex', alignItems: 'center', gap: 8}}>
                    <i className="pi pi-history" style={{color: '#0891b2'}}/>
                    {t('dynamic_pricing_audit.title')}
                </span>
                <Button label={t('dynamic_pricing_audit.see_all')} icon="pi pi-list"
                        className="p-button-text p-button-sm"
                        onClick={() => setShowAll(true)}/>
            </div>
            <small style={{color: '#6b7280', display: 'block', marginBottom: 10}}>
                {t('dynamic_pricing_audit.hint')}
            </small>

            {loading && <small style={{color: '#6b7280'}}>…</small>}
            {!loading && rows.length === 0 &&
                <small style={{color: '#9ca3af'}}>{t('dynamic_pricing_audit.empty')}</small>}

            {!loading && rows.map(row => (
                <div key={row.id} style={{display: 'flex', alignItems: 'flex-start', gap: 10,
                                          padding: '8px 0', borderTop: '1px solid #f3f4f6'}}>
                    <Tag value={scopeLabel(t, row.scope)} severity={scopeSeverity(row.scope)}/>
                    <div style={{flex: 1, minWidth: 0}}>
                        <div style={{fontSize: '.85rem', color: '#111827'}}>
                            <strong>{row.actor_name || row.actor_email}</strong>
                            {row.is_impersonated &&
                                <span style={{marginLeft: 6, fontSize: '.7rem', color: '#b45309',
                                              border: '1px solid #fcd34d', borderRadius: 4,
                                              padding: '0 4px'}}>
                                    {t('dynamic_pricing_audit.impersonated')}
                                </span>}
                            <span style={{color: '#6b7280'}}> · {actionLabel(t, row)}</span>
                            {row.target_label && <span style={{color: '#6b7280'}}> · {row.target_label}</span>}
                        </div>
                        <div style={{fontSize: '.78rem', color: '#6b7280', overflow: 'hidden',
                                     textOverflow: 'ellipsis', whiteSpace: 'nowrap'}}>
                            {changeSummary(t, row)}
                        </div>
                    </div>
                    <small style={{color: '#9ca3af', whiteSpace: 'nowrap'}}
                           title={new Date(row.created_at).toLocaleString()}>
                        {toRelativeTimeString(row.created_at)}
                    </small>
                </div>
            ))}

            <DynamicPricingAuditDialog t={t} campaignId={campaignId}
                                       visible={showAll} onHide={() => setShowAll(false)}/>
        </div>
    );
};

export default DynamicPricingAuditCard;
